import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DashNav from "@/components/chalann/DashNav";
import { dailyReport, formatMXN, getStore, getUser, getInventoryReport } from "@/lib/api";
import { TrendingUp, TrendingDown, Clock3, Award, ArrowLeft, Wallet, CreditCard, BookMarked, Package, ShieldOff, Boxes, AlertOctagon } from "lucide-react";
import { Toaster } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";

export default function Reporte() {
  const nav = useNavigate();
  const store = getStore();
  const user = getUser();
  const isStaff = user?.role === "staff";
  const [data, setData] = useState(null);
  const [inv, setInv] = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadInventory = async () => {
    setInvLoading(true);
    try { setInv(await getInventoryReport()); }
    catch { /* noop */ }
    finally { setInvLoading(false); }
  };

  useEffect(() => {
    if (!store?.id) return nav("/setup");
    let cancelled = false;
    const load = async () => {
      try {
        const r = await dailyReport();
        if (!cancelled) setData(r);
      } catch (e) {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [nav, store?.id]);

  const today = data?.today;
  const yest = data?.yesterday;
  const deltas = data?.deltas;

  const hourlyData = today?.hourly?.map((v, h) => ({
    hour: `${h.toString().padStart(2, "0")}h`,
    revenue: v,
    isPeak: today.peak_hour === h,
  })) || [];

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="reporte">
      <div className="grain-overlay" />
      <DashNav />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <button onClick={() => nav("/dashboard")} className="text-white/50 hover:text-white text-xs font-mono uppercase tracking-widest flex items-center gap-1 mb-4" data-testid="back-btn">
          <ArrowLeft size={14} /> Volver al panel
        </button>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2">
              / {isStaff ? `Turno de ${user?.name}` : "Corte del día"}
            </p>
            <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none">
              {isStaff ? (
                <>Tu turno en <span className="text-[#ccff00]">{store?.name}.</span></>
              ) : (
                <>El día en <span className="text-[#ff5a00]">{store?.name || "tu tienda"}.</span></>
              )}
            </h1>
          </div>
          <p className="font-mono text-xs text-white/40 tracking-widest">
            {new Date().toLocaleString("es-MX", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" }).toUpperCase()}
          </p>
        </div>

        {isStaff && (
          <div className="mb-6 rounded-2xl border border-[#ccff00]/25 bg-[#ccff00]/[0.04] p-4 flex items-center gap-3" data-testid="staff-notice">
            <ShieldOff size={16} className="text-[#ccff00] flex-shrink-0" />
            <p className="text-sm text-white/80">
              Vista de empleado: solo muestra <strong>lo que tú cobraste hoy</strong>. Ganancia, costos y fiados generales están reservados al dueño.
            </p>
          </div>
        )}

        {loading || !today ? (
          <div className="text-center text-white/40 py-16">Cargando corte…</div>
        ) : (
          <>
            {/* Hero comparison */}
            <motion.section
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
            >
              <div className={`${isStaff ? "md:col-span-3" : "md:col-span-2"} rounded-3xl border border-white/10 bg-gradient-to-br from-[#ff5a00]/[0.15] to-transparent p-8 md:p-10 relative overflow-hidden`}>
                <div className="glow-orange" style={{ bottom: "-60%", right: "-30%" }} />
                <div className="relative">
                  <p className="font-mono text-[11px] tracking-widest text-white/60 uppercase">
                    {isStaff ? `Cobrado por ${user?.name}` : "Vendido hoy"}
                  </p>
                  <p className="font-display text-6xl md:text-8xl tracking-tighter leading-none mt-2" data-testid="hero-total">
                    {formatMXN(today.total)}
                  </p>
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <DeltaChip label="vs ayer" value={deltas?.total_pct} />
                    <span className="chip">{today.count} tickets · {today.items_sold} artículos</span>
                    <span className="chip">Ticket promedio {formatMXN(today.avg_ticket)}</span>
                  </div>
                </div>
              </div>

              {!isStaff && (
                <div className="rounded-3xl border border-[#ccff00]/30 bg-gradient-to-br from-[#ccff00]/[0.12] to-transparent p-6 md:p-8 relative overflow-hidden" data-testid="ganancia-card">
                  <p className="font-mono text-[11px] tracking-widest text-white/60 uppercase">Ganancia estimada</p>
                  <p className="font-display text-4xl md:text-5xl tracking-tighter leading-none mt-2 text-[#ccff00]">
                    {formatMXN(today.profit)}
                  </p>
                  <div className="mt-3">
                    <DeltaChip label="vs ayer" value={deltas?.profit_pct} />
                  </div>
                  <p className="mt-4 text-[11px] font-mono text-white/40 uppercase tracking-widest border-t border-white/10 pt-3">
                    Precio venta − costo capturado en inventario
                  </p>
                </div>
              )}
            </motion.section>

            {/* Row: peak hour + payment mix + top */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Hourly chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
                className="lg:col-span-8 rounded-3xl border border-white/10 bg-white/[0.02] p-6"
                data-testid="hourly-chart"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-mono text-[11px] tracking-widest text-white/40 uppercase">Ventas por hora</p>
                    <h2 className="font-display text-2xl md:text-3xl tracking-tighter mt-1">Ritmo de la tienda</h2>
                  </div>
                  {today.peak_hour !== null && today.peak_hour !== undefined && (
                    <div className="text-right">
                      <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase flex items-center gap-1 justify-end">
                        <Clock3 size={11} /> Hora pico
                      </p>
                      <p className="font-display text-3xl tracking-tighter mt-1 text-[#ff5a00]" data-testid="peak-hour">
                        {String(today.peak_hour).padStart(2, "0")}:00
                      </p>
                      <p className="text-xs text-white/50 font-mono">{formatMXN(today.peak_hour_amount)}</p>
                    </div>
                  )}
                </div>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart data={hourlyData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                      <XAxis dataKey="hour" tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} interval={2} />
                      <YAxis tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => `$${Math.round(v)}`} />
                      <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.04)" }}
                        contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, fontFamily: "JetBrains Mono", fontSize: 11 }}
                        formatter={(v) => formatMXN(v)}
                        labelStyle={{ color: "#f5f5f5" }}
                      />
                      <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                        {hourlyData.map((d, i) => (
                          <Cell key={i} fill={d.isPeak ? "#ff5a00" : "#ccff00"} fillOpacity={d.isPeak ? 1 : 0.55} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              {/* Payment mix */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }}
                className="lg:col-span-4 rounded-3xl border border-white/10 bg-white/[0.02] p-6"
                data-testid="payment-mix"
              >
                <p className="font-mono text-[11px] tracking-widest text-white/40 uppercase mb-4">Métodos de pago</p>
                <PaymentRow icon={Wallet} label="Efectivo" amount={today.cash} total={today.total} color="#ccff00" />
                <PaymentRow icon={CreditCard} label="Tarjeta" amount={today.card} total={today.total} color="#f5f5f5" />
                <PaymentRow icon={BookMarked} label="Fiado" amount={today.credit} total={today.total} color="#ff5a00" />
                <div className="mt-4 pt-4 border-t border-white/10 flex justify-between text-sm">
                  <span className="text-white/60">Total</span>
                  <span className="font-display text-xl tracking-tighter">{formatMXN(today.total)}</span>
                </div>
              </motion.div>
            </div>

            {/* Top products */}
            <motion.section
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
              className="mt-6 rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8"
              data-testid="top-products"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="font-mono text-[11px] tracking-widest text-white/40 uppercase">Los que vuelan hoy</p>
                  <h2 className="font-display text-2xl md:text-3xl tracking-tighter mt-1 flex items-center gap-2">
                    <Award className="text-[#ff5a00]" size={22} /> Top 5 productos
                  </h2>
                </div>
              </div>
              {today.top_products.length === 0 ? (
                <p className="py-8 text-center text-white/40 text-sm border border-dashed border-white/10 rounded-2xl">
                  Aún no hay ventas hoy. En cuanto empieces a cobrar aquí verás los favoritos.
                </p>
              ) : (
                <div className="space-y-2">
                  {today.top_products.map((p, i) => {
                    const pct = today.top_products[0].revenue > 0 ? (p.revenue / today.top_products[0].revenue) * 100 : 0;
                    return (
                      <div key={p.product_id || p.name || i} className="rounded-xl border border-white/5 bg-black/30 p-3 flex items-center gap-4" data-testid={`top-${i}`}>
                        <span className="font-display text-2xl tracking-tighter w-8 text-white/40">{String(i + 1).padStart(2, "0")}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.name}</p>
                          <p className="text-[11px] font-mono text-white/50">{p.quantity} vendidos</p>
                          <div className="mt-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full rounded-full"
                              initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                              transition={{ duration: 0.9, delay: 0.2 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                              style={{ background: i === 0 ? "#ff5a00" : "#ccff00" }}
                            />
                          </div>
                        </div>
                        <span className="font-display text-lg tracking-tighter">{formatMXN(p.revenue)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.section>

            {/* Yesterday snapshot — owner only */}
            {!isStaff && (
              <motion.section
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7 }}
                className="mt-6 rounded-3xl border border-white/5 bg-white/[0.015] p-6 md:p-8"
                data-testid="yesterday"
              >
                <p className="font-mono text-[11px] tracking-widest text-white/40 uppercase mb-3">Ayer, para comparar</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <YestStat label="Vendido" value={formatMXN(yest?.total)} />
                  <YestStat label="Tickets" value={yest?.count ?? 0} />
                  <YestStat label="Ganancia" value={formatMXN(yest?.profit)} />
                  <YestStat
                    label="Top de ayer"
                    value={yest?.top_products?.[0]?.name || "—"}
                    small
                  />
                </div>
              </motion.section>
            )}
          </>
        )}

        {/* Reporte de inventario on-demand */}
        <motion.section
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-12 border-t border-white/10 pt-10"
          data-testid="inventory-report-section"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-1">/ Reporte de inventario</p>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-none">
                Lo que hay <span className="text-[#00b3ff]">hoy</span>.
              </h2>
            </div>
            <button
              onClick={loadInventory}
              disabled={invLoading}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#00b3ff] hover:bg-[#33c4ff] text-black font-bold text-sm disabled:opacity-60"
              data-testid="btn-inventory-report"
            >
              <Boxes size={16} strokeWidth={2.5} /> {inv ? "Actualizar" : "Generar reporte"}
            </button>
          </div>

          {!inv ? (
            <p className="text-white/40 text-sm">Toca &ldquo;Generar reporte&rdquo; para ver el estado del inventario.</p>
          ) : (
            <div className="space-y-6" data-testid="inventory-report">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InvStat label="Productos" value={inv.totals.products} tid="inv-total-products" />
                <InvStat label="Unidades" value={inv.totals.units.toLocaleString("es-MX")} tid="inv-total-units" />
                <InvStat label="Agotados" value={inv.totals.out_of_stock} tint={inv.totals.out_of_stock ? "text-[#ff5a00]" : "text-white"} tid="inv-out" />
                <InvStat label="Stock bajo" value={inv.totals.low_stock} tint={inv.totals.low_stock ? "text-[#ccff00]" : "text-white"} tid="inv-low" />
                {!isStaff && (
                  <>
                    <InvStat label="Valor a costo" value={formatMXN(inv.totals.cost_value_mxn)} tid="inv-cost-value" />
                    <InvStat label="Valor a venta" value={formatMXN(inv.totals.sale_value_mxn)} tid="inv-sale-value" />
                    <InvStat label="Ganancia potencial" value={formatMXN(inv.totals.potential_profit_mxn)} tint="text-emerald-400" tid="inv-profit" />
                    <InvStat label="Categorías" value={inv.totals.categories} tid="inv-cats" />
                  </>
                )}
              </div>

              {inv.categories?.length > 0 && (
                <div>
                  <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-3">Por categoría</p>
                  <div className="border border-white/10 rounded-2xl overflow-hidden">
                    <div className={`grid ${isStaff ? "grid-cols-3" : "grid-cols-5"} px-4 py-2 bg-white/5 text-[10px] font-mono uppercase tracking-widest text-white/50`}>
                      <span>Categoría</span><span>Productos</span><span>Unidades</span>
                      {!isStaff && <><span>Valor costo</span><span>Valor venta</span></>}
                    </div>
                    {inv.categories.map((c) => (
                      <div key={c.category} className={`grid ${isStaff ? "grid-cols-3" : "grid-cols-5"} items-center px-4 py-2 border-t border-white/5 text-sm`} data-testid={`inv-cat-${c.category}`}>
                        <span className="font-display tracking-tight">{c.category}</span>
                        <span className="font-mono">{c.count}</span>
                        <span className="font-mono">{c.units.toLocaleString("es-MX")}</span>
                        {!isStaff && <><span className="font-mono">{formatMXN(c.cost_value)}</span><span className="font-mono text-[#ccff00]">{formatMXN(c.sale_value)}</span></>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {inv.low_stock?.length > 0 && (
                <div>
                  <p className="font-mono text-[10px] tracking-widest text-[#ff5a00] uppercase mb-3 flex items-center gap-1"><AlertOctagon size={12} /> Necesita reposición</p>
                  <div className="border border-[#ff5a00]/30 rounded-2xl overflow-hidden bg-[#ff5a00]/5">
                    {inv.low_stock.slice(0, 10).map((p) => (
                      <div key={p.id} className="grid grid-cols-[1fr_140px_140px] items-center px-4 py-2 border-t border-white/5 first:border-t-0 text-sm" data-testid={`inv-low-${p.id}`}>
                        <span className="truncate">{p.name}</span>
                        <span className="font-mono text-[#ff5a00]">{p.stock_quantity} {p.unit}</span>
                        <span className="font-mono text-white/50 text-xs">min: {p.min_stock_alert}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.section>
      </div>
      <Toaster theme="dark" position="top-center" />
    </main>
  );
}

function DeltaChip({ label, value }) {
  if (value === null || value === undefined) {
    return <span className="chip"><Package size={11} /> {label}: primer día</span>;
  }
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive ? "#ccff00" : "#ff5a5a";
  return (
    <span className="chip" style={{ color }}>
      <Icon size={11} /> {label} {positive ? "+" : ""}{value}%
    </span>
  );
}

function PaymentRow({ icon: Icon, label, amount, total, color }) {
  const pct = total > 0 ? (amount / total) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-2 text-sm text-white/80"><Icon size={14} style={{ color }} /> {label}</span>
        <span className="font-mono text-sm">{formatMXN(amount)}</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7 }} style={{ background: color }} />
      </div>
    </div>
  );
}

function YestStat({ label, value, small }) {
  return (
    <div>
      <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase">{label}</p>
      <p className={`mt-1 font-display tracking-tighter ${small ? "text-base" : "text-2xl"}`}>{value}</p>
    </div>
  );
}

function InvStat({ label, value, tint, tid }) {
  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4" data-testid={tid}>
      <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase">{label}</p>
      <p className={`mt-2 font-display text-3xl md:text-4xl tracking-tighter leading-none ${tint || "text-white"}`}>{value}</p>
    </div>
  );
}

